/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

import { useTranslation } from 'react-i18next';

/**
 * Use this Hook for your onError methods for requests to magicline
 *
 *
 * @returns {{getErrorMessage: ((function(*, *): (string))|*)}}
 */
export default function useErrorCodeMapping() {
  const { t } = useTranslation();

  /**
   * Map the given errorCodes to the error message in the translation
   *
   * @param errorCodes
   * @returns {string}
   */
  const mapErrorCodesToMessages = (errorCodes) => {
    let mappedErrorMessages = '';
    errorCodes.forEach((errorCode) => {
      const mappedErrorMessage = t(errorCode);
      // Only add the mapped message, if a translation is available
      if (errorCode !== mappedErrorMessage) {
        mappedErrorMessages += ` ${mappedErrorMessage}`;
      }
    });
    return mappedErrorMessages;
  };

  /**
   * Since magicline don't provide translations for their error messages,
   * we need to map the error codes to our own labels
   *
   * @param originalErrorMessage
   * @param errorCodes
   */
  const getErrorMessage = (originalErrorMessage, errorCodes) => {
    // Fallback error message
    const fallbackMessage = t('MAGICLINE_FALLBACK_MESSAGE');
    if (!errorCodes || !Array.isArray(errorCodes)) {
      return fallbackMessage;
    }
    const errorMessage = mapErrorCodesToMessages(errorCodes);
    // Only return the mapped error message if any translations for the given codes were found
    return errorMessage === '' ? fallbackMessage : errorMessage;
  };

  return { getErrorMessage };
}
